import React, { FC, ReactElement, useEffect } from 'react';
import { connect } from 'react-redux';
import StateInterface from '../../redux/types';
import { ListingPageUrlState, ProductInterface } from '../../redux/products/types';
import useListingPageUrlState from '../../hooks/ListingPage/useListingPageUrlState';
import useGa from '../../hooks/useGa';
import { ContentInfusionsAllowedPageTypes, PageType } from '../../hooks/UrlManager/types';
import usePrevious from '../../hooks/usePrevious';
import { fetchProductsDataIfNeeded } from '../../redux/products/actions';
import { fetchContentInfusionIfNeeded } from '../../redux/infusions/actions';
import { NormalizedCategoriesData } from '../../redux/category/types';
import useTrackingPageLabel from '../../hooks/useTrackingPageLabel';
import { useUniversalEffect } from '../../hooks/useUniversalEffect';
import { getDefaultSeoData } from '../../hooks/ListingPage/seo/seoUtils';
import useFetchingAction from '../../hooks/useFetchingAction/useFetchingAction';
import useConfig from '../../hooks/useConfig';
import useBellaTranslator from '../../hooks/useTranslator';
import { combineThunks } from '../../redux/utils';
import { categoryActions } from '../../redux/category/actions';
import { fetchSeoDataIfNeeded } from '../../redux/seo/actions';
import { useBrazePlpEvent } from '../../hooks/Braze/useBraze';
import { fetchCMSLinkedBannersIfNeeded } from '../../redux/cms/actions';
import { SHOP_TO_CLUB_FOOTER_LINK } from '../../redux/cms/constants';
import { Dictionary } from '../../../../types/globals';
import { brandActions } from '../../redux/brands/actions';
import { getEntityByUrlKey } from '../../redux/category/categoriesNormalizer';
import ListingPage from './ListingPage';
import NoResultsPage from './NoResults';
import NoFilterResultsPage from './NoFilterResults';
import useLandingPageRedirects from './redirectsHook';

interface ReduxStateProps {
    isFetching: boolean;
    isFetchingComplete?: boolean;
    products: ProductInterface[];
    brandEntities: Dictionary<string> | {};
    categories: NormalizedCategoriesData | null;
}

const buildGaData = (
    urlState: ListingPageUrlState,
    categories: NormalizedCategoriesData | null,
    brandEntities: Dictionary<string> | {}
): Dictionary<string> => {
    /* eslint-disable @typescript-eslint/camelcase, camelcase */
    const { page, listingPageKey } = urlState;
    const pageViewData = { page_number: page.toString() };
    switch (urlState.listingPageType) {
        case PageType.CATEGORY:
            return {
                categories_path: listingPageKey,
                category_id: categories ? categories.urlKeys[listingPageKey] : '',
                ...pageViewData,
            };
        case PageType.BRANDS:
            return { brand_id: brandEntities[listingPageKey] || '', ...pageViewData };
        default:
            return pageViewData;
    }
    /* eslint-enable @typescript-eslint/camelcase, camelcase */
};

// eslint-disable-next-line complexity
const DynamicListingPage: FC<{} & ReduxStateProps> = (props): ReactElement | null => {
    const { isFetching, products, categories, brandEntities, isFetchingComplete } = props;
    const [urlState] = useListingPageUrlState();
    const pageType = useTrackingPageLabel();

    const gaTracking = useGa();
    const amFiltering = urlState.appliedFilters.length > 0;
    const isAllProducts = urlState.listingPageType === PageType.ALLPRODUCTS;
    const isNoResults = isAllProducts && isFetchingComplete && !products.length;
    const isNoFilterResults = !isFetching && isFetchingComplete && !products.length && amFiltering;

    const wasNoResults = usePrevious(isNoResults);
    const didNoResultsBecomeTrue = !wasNoResults && isNoResults;

    const config = useConfig();
    const t = useBellaTranslator();
    const thunks = [
        fetchProductsDataIfNeeded(urlState, { countryCode: config.countryCode, currency: config.currency }),
        categoryActions.thunk(),
        fetchSeoDataIfNeeded(urlState, getDefaultSeoData(urlState.listingPageType, t, config)),
        fetchCMSLinkedBannersIfNeeded(SHOP_TO_CLUB_FOOTER_LINK),
    ];

    const infusionEnabled = ContentInfusionsAllowedPageTypes.includes(urlState.listingPageType);

    // Fetch Content infusion only for category pages with no filters and only on thr first page
    if (infusionEnabled && urlState.appliedFilters.length === 0 && urlState.page === 1) {
        thunks.push(fetchContentInfusionIfNeeded(urlState));
    }

    if (urlState.listingPageType === PageType.BRANDS) {
        thunks.push(brandActions.thunk());
    }

    const gaData = buildGaData(urlState, categories, brandEntities);

    useFetchingAction(combineThunks(...thunks));

    useEffect(() => {
        if (didNoResultsBecomeTrue) {
            gaTracking.trackSearch({
                action: gaTracking.actions.Search.NoResultPage,
                label: (urlState.search || '').toLocaleLowerCase(),
            });
        }
    }, [didNoResultsBecomeTrue, gaTracking, urlState.search]);

    useEffect(() => {
        const query = urlState.search;

        if (query) {
            gaTracking.trackECommerce({
                action: gaTracking.actions.ECommerce.PlpUrlSearchTerm,
                label: query.toLocaleLowerCase(),
            });
        }
    }, [gaTracking, urlState.search]);

    useUniversalEffect(() => {
        gaTracking.trackPageView(pageType, gaData);
    }, [pageType, urlState]);

    const categoryEntity = getEntityByUrlKey(categories, urlState.listingPageKey);
    useBrazePlpEvent(categoryEntity);
    const wasRedirected = useLandingPageRedirects();

    if (wasRedirected) {
        return null;
    }
    if (isNoResults) {
        return <NoResultsPage />;
    }
    if (isNoFilterResults) {
        return <NoFilterResultsPage />;
    }
    return <ListingPage />;
};

const mapStateToProps = (state: StateInterface): ReduxStateProps => ({
    isFetching: state.products.isFetching,
    isFetchingComplete: state.products.isFetchingComplete,
    products: state.products.products,
    brandEntities: state.brands.brandEntities,
    categories: state.categories.normalized,
});

const Connected = connect(mapStateToProps)(DynamicListingPage);

export default Connected;
